








import { SfLoader, SfHeading } from "@storefront-ui/vue";
import {
  defineComponent,
  ref,
  useFetch,
  useContext,
} from "@nuxtjs/composition-api";
import { useCache, CacheTagPrefix } from "@vue-storefront/cache";
import { getMetaInfo } from "~/helpers/getMetaInfo";
import { useContent } from "~/composables";
import type { CmsPage } from "~/modules/GraphQL/types";
import { usePageStore } from "~/stores/page";
import HTMLContent from "~/components/HTMLContent.vue";

interface ComponentData {
  processedContent: string;
  extractedScripts: Array<{ src?: string; content?: string; type?: string }>;
  executedScriptHashes: string[];
  contentReady: boolean;
  scriptsReady: boolean;
  pageReady: boolean;
}

export default defineComponent({
  name: "CmsPage",
  components: {
    HTMLContent,
    SfLoader,
    SfHeading,
  },
  data(): ComponentData {
    return {
      processedContent: "",
      extractedScripts: [],
      executedScriptHashes: [],
      contentReady: true,
      scriptsReady: false,
      pageReady: false,
    };
  },
  setup() {
    const { routeData } = usePageStore();
    const { addTags } = useCache();
    const { error: nuxtError } = useContext();
    const { loadPage, loading, error } = useContent();

    const page = ref<CmsPage | null>(null);

    useFetch(async () => {
      page.value = await loadPage({ identifier: routeData.identifier });

      if (error?.value?.page || !page.value) nuxtError({ statusCode: 404 });

      addTags([{ prefix: CacheTagPrefix.View, value: routeData.identifier }]);
    });

    return {
      page,
      loading,
    };
  },
  watch: {
    "page.content": {
      handler(this: any, newContent: string) {
        console.log('[DEBUG] Page content changed:', !!newContent);
        if (newContent) {
          this.pageReady = true;
          console.log('[DEBUG] Content rendered, now loading scripts...');
          this.$nextTick(() => {
            this.loadScriptsFirst();
          });
        }
      },
      immediate: true,
    },
  },
  computed: {
    cleanContent(this: any): string {
      return this.page?.content || "";
    },
  },
  mounted() {
    console.log('[DEBUG] Component mounted');
    // Scripts will be loaded after page content is ready via watcher
  },
  methods: {
    loadScriptsFirst() {
      console.log('[DEBUG] loadScriptsFirst called');
      
      const loadScript = (src: string): Promise<void> => {
        return new Promise((resolve, reject) => {
          console.log(`[DEBUG] Loading script: ${src}`);
          const script = document.createElement("script");
          script.src = src;
          script.onload = () => {
            console.log(`[DEBUG] Script loaded successfully: ${src}`);
            resolve();
          };
          script.onerror = () => {
            console.error(`[DEBUG] Script failed to load: ${src}`);
            reject(new Error(`Failed to load script: ${src}`));
          };
          document.body.appendChild(script);
        });
      };

      loadScript(
        "https://ajax.googleapis.com/ajax/libs/jquery/2.1.1/jquery.min.js"
      )
        .then(() => {
          console.log('[DEBUG] jQuery loaded, loading Bootstrap Validator...');
          return loadScript(
            "https://cdn.jsdelivr.net/jquery.bootstrapvalidator/0.5.0/js/bootstrapValidator.min.js"
          );
        })
        .then(() => {
          console.log('[DEBUG] Bootstrap Validator loaded, loading Swiper...');
          return loadScript(
            "https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.js"
          );
        })
        .then(() => {
          console.log('[DEBUG] All scripts loaded, executing custom JS...');
          this.initCustomJS();
          this.scriptsReady = true;
          console.log('[DEBUG] All scripts and custom JS ready');
        })
        .catch((err) => {
          console.error("Script load error:", err);
          this.scriptsReady = true;
        });
    },
    initCustomJS() {
      console.log('[DEBUG] initCustomJS called');
      const metaInfo = getMetaInfo(this.page) || {};
      console.log('[DEBUG] metaInfo:', metaInfo);

      const jsContent = metaInfo?.meta?.find(
        (item) => item.hid === "keywords"
      )?.content;

      console.log('[DEBUG] jsContent:', jsContent);

      try {
        if (jsContent) {
          console.log('[DEBUG] Executing custom JS content...');
          eval(jsContent);
          console.log('[DEBUG] Custom JS executed successfully');
        } else {
          console.log('[DEBUG] No custom JS content found');
        }
      } catch (error) {
        console.error("[JSContent Error]", error);
      }
    },
  },
  head(this: any) {
    const metaInfo = getMetaInfo(this.page) || {};

    const scripts = [...(metaInfo.script || [])];

    // Remove script extraction logic since we're not processing content anymore
    return {
      title: metaInfo.title || "",
      meta: (metaInfo.meta || []).filter((item) => item.hid !== "keywords"),
      script: scripts,
    };
  },
});
